import { observable, action, makeObservable } from 'mobx';

import agent from '../agent';

import userStore from './userStore';
import commonStore from './commonStore';

class AuthStore {
    inProgress = false;
    errors = undefined;
    frontErrors = undefined;
    values = {
        username: '',
        password: '',
        name: '',
        confirmPassword: '',
        email: '',
        userType: 'client',
    };

    additionalData = {};

    custConfig = {};

    constructor() {
        makeObservable(this, {
            inProgress: observable,
            errors: observable,
            frontErrors: observable,
            values: observable,
            additionalData: observable,
            custConfig: observable,
            formChange: action,
            setUsername: action,
            setEmail: action,
            setPassword: action,
            setData: action,
            setError: action,
            reset: action,
            login: action,
            loginAs: action,
            logout: action,
            resetPassword: action,
            showAvailableClients: action
        });
    }

    formChange(name, value) {
        this.values[name] = value;
    }

    setUsername(username) {
        this.values.username = username;
    }

    setEmail(email) {
        this.values.email = email;
    }

    setPassword(password) {
        this.values.password = password;
    }

    setData(name, value) {
        this.additionalData[name] = value;
    }

    setError(err) {
        this.frontErrors = err;
    }

    reset() {
        this.values = {
            username: '',
            password: '',
            name: '',
            confirmPassword: '',
            email: '',
            userType: 'client',
        };
        this.errors = null;
        this.frontErrors = null;
        this.additionalData = {};
    }

    login() {
        this.inProgress = true;
        this.errors = undefined;
        return agent.Auth.login(this.values.email, this.values.password)
            .then(({ user }) => {
                if (user.requires2FACode) {
                    window.sessionStorage.setItem('x-1fa-token', user.token)
                    return user
                } else if (user.requires2FASetup) {
                    window.sessionStorage.setItem('x-setup-2fa-token', user.token)
                    return user
                } else {
                    return commonStore.setToken(user.token)
                }
            })
            .then((user) => {
                if (user && user.requires2FACode) {
                    return user
                } else if (user && user.requires2FASetup) {
                    return user
                } else {
                    return userStore.pullUser()
                }
            })
            .catch(
                action(err => {
                    if (err?.response?.body?.errors) {
                        this.errors = err.response.body.errors.error;
                    }
                    throw err;
                })
            )
            .finally(
                action(() => {
                    this.inProgress = false;
                })
            );
    }

    loginWithSkip2FASetup(skip2FASetup = false) {
        this.inProgress = true;
        this.errors = undefined;
        return agent.Auth.loginWithSkip2FASetup(skip2FASetup)
            .then(({ user }) => {
                return commonStore.setToken(user.token)
            })
            .then(() => {
                return userStore.pullUser()
            })
            .catch(
                action(err => {
                    if (err?.response?.body?.errors) {
                        this.errors = err.response.body.errors.error;
                    }
                    throw err;
                })
            )
            .finally(
                action(() => {
                    this.inProgress = false;
                })
            );
    }

    loginWith2FAVerificationCode(verificationCode = '') {
        this.inProgress = true;
        this.errors = undefined;
        return agent.Auth.loginWith2FAVerificationCode(verificationCode)
            .then(({ user }) => {
                return commonStore.setToken(user.token)
            })
            .then(() => {
                return userStore.pullUser()
            })
            .catch(
                action(err => {
                    if (err?.response?.body?.errors) {
                        this.errors = err.response.body.errors.error;
                    }
                    throw err;
                })
            )
            .finally(
                action(() => {
                    this.inProgress = false;
                })
            );
    }

    setup2FA() {
        return agent.Auth.setup2FA()
            .then((res) => {
                return res
            })
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    enable2FA(verificationCode, source) {
        return agent.Auth.enable2FA(verificationCode, source)
            .then((res) => {
                if (res && res.token) {
                    commonStore.setToken(res.token);
                }
                return res;
            })
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    disable2FA(verificationCode, comment) {
        return agent.Auth.disable2FA(verificationCode, comment)
            .then((res) => {
                if (res && res.token) {
                    commonStore.setToken(res.token);
                }
                return res
            })
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    getRecover2FA() {
        return agent.Auth.getRecover2FA()
            .then((res) => {
                return res
            })
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    recover2FA(otp, comment) {
        return agent.Auth.recover2FA(otp, comment)
            .then((res) => {
                return res
            })
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    loginAs(id) {
        this.inProgress = true;
        this.errors = undefined;
        return agent.Auth.loginAs(id)
            .then(response => {
                if (response.user && response.user.token) {
                    commonStore.setToken(response.user.token);
                    userStore.pullUser();
                    userStore.removeMultiAdminToken();
                } else if (response && response.status === 'requested') {
                    throw new Error('Request to user is sent by email, you will be notified when one accepts');
                }
            })
            .catch(
                action(err => {
                    this.errors = err?.response?.body?.errors;
                    throw err;
                })
            )
            .finally(
                action(() => {
                    this.inProgress = false;
                })
            );
    }

    logout() {
        commonStore.setToken(undefined);
        userStore.forgetUser();
        return Promise.resolve();
    }

    resetPassword(email) {
        return agent.Auth.resetPassword(email).catch(e => {
            return false;
        });
    }

    forgotPasswordLink(email) {
        return agent.Auth.forgotPassword(email).catch(err => {
            throw err;
        });
    }

    resetPasswordViaLink(token, password) {
        return agent.Auth.resetPasswordViaLink(token, password).catch(err => {
            throw err;
        });
    }

    signInWithEmail(value) {
        return agent.External.signInWithEmail(value)
            .catch(
                action(err => {
                    this.errors = err?.response?.body?.errors;
                    throw err;
                })
            )
    }

    loginWithOtp(value) {
        return agent.External.loginWithOtp(value)
            .then(
                action(res => {
                    window.sessionStorage.setItem('cusToken', res.token)
                    window.sessionStorage.setItem('role', res.role)
                    return true
                })
            )
            .catch(
                action(err => {
                    this.errors = err?.response?.body?.errors;
                    throw err;
                })
            )
    }

    loadCustConfig() {
        return agent.External.loadCustConfig()
            .then(
                action(res => {
                    this.custConfig = res;
                })
            )
            .catch(
                action(err => {
                    this.errors = err?.response?.body?.errors;
                    throw err;
                })
            )
    }

    showAvailableClients(email, role) {
        return agent.External.getAvailableClients(email, role)
            .then(
                action(data => {
                    return data;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }
}

const _AuthStore = new AuthStore();
export default _AuthStore;
